import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { artworkService } from '../../services/artworkService';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';

const HeroContainer = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.gallery.neutrals.darkest};
`;

const ArtworkImage = styled(ArtworkObjectImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const PaletteMark = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
    <path d="M12 3.5c-4.7 0-8.5 3.28-8.5 7.33 0 3.35 2.66 6.17 6.28 7.05.74.18 1.16-.26 1.16-.78 0-.34-.12-.69-.12-1.04 0-.86.7-1.56 1.56-1.56h1.7c3.58 0 6.42-2.39 6.42-5.35 0-3.12-3.8-5.65-8.5-5.65Z" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="8" cy="9" r="1" fill="currentColor" />
    <circle cx="11" cy="7.25" r="1" fill="currentColor" />
    <circle cx="14.5" cy="8" r="1" fill="currentColor" />
    <circle cx="16.5" cy="11" r="1" fill="currentColor" />
  </svg>
);

const ContentOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    transparent 0%,
    rgba(40, 40, 32, 0.4) 40%,
    rgba(40, 40, 32, 0.8) 100%
  );
  padding: ${({ theme }) => theme.spacing.gallery.xl} ${({ theme }) => theme.spacing.gallery.md};
  color: ${({ theme }) => theme.colors.white};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.gallery.lg} ${({ theme }) => theme.spacing.gallery.sm};
  }
`;

const ExhibitionBadge = styled(motion.div)`
  display: inline-block;
  background: rgba(132, 147, 160, 0.9); /* Blue Gray with transparency */
  backdrop-filter: blur(10px);
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.medium};
  margin-bottom: ${({ theme }) => theme.spacing.gallery.sm};
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ExhibitionTitle = styled(motion.h1)`
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.hero};
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.bold};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  margin-bottom: ${({ theme }) => theme.spacing.gallery.sm};
  color: ${({ theme }) => theme.colors.white};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.display};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
  }
`;

const StudentName = styled(motion.p)`
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.normal};
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const NavigationDots = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.gallery.md};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: ${({ theme }) => theme.spacing.gallery.xs};
  z-index: 10;
`;

const NavigationDot = styled.button<{ $isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: ${({ $isActive }) => 
    $isActive ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.gallery.quick};
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(255, 255, 255, 0.8);
    transform: scale(1.2);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(132, 147, 160, 0.4);
  }
`;

const NavigationArrows = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing.gallery.md};
  pointer-events: none;
  z-index: 10;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none; /* Hide arrows on mobile, rely on dots */
  }
`;

const ArrowButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(40, 40, 32, 0.6);
  backdrop-filter: blur(10px);
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  pointer-events: auto;
  transition: all ${({ theme }) => theme.transitions.gallery.quick};
  
  &:hover {
    background: rgba(40, 40, 32, 0.8);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.1);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(132, 147, 160, 0.4);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.gallery.neutrals.medium} 0%, 
    ${({ theme }) => theme.colors.gallery.neutrals.dark} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.white};
`;

const LoadingIcon = styled(motion.div)`
  margin-bottom: ${({ theme }) => theme.spacing.gallery.sm};
  opacity: 0.7;

  svg {
    width: 64px;
    height: 64px;
  }
`;

const LoadingText = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
  opacity: 0.8;
  margin: 0;
`;

interface HeroArtworkRotationProps {
  artworks: Array<{
    artwork_id: number;
    student_name?: string;
    uploaded_at: string;
    updated_at?: string;
  }>;
  exhibitionTitle: string;
  autoRotateInterval?: number;
  className?: string;
}

export function HeroArtworkRotation({ 
  artworks, 
  exhibitionTitle,
  autoRotateInterval = 5000,
  className 
}: HeroArtworkRotationProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Auto-rotation logic
  useEffect(() => {
    if (!artworks || artworks.length <= 1 || isHovered) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % artworks.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [artworks, autoRotateInterval, isHovered]);

  // Preload images for smooth transitions
  useEffect(() => {
    if (!artworks || artworks.length === 0) return;

    const preloadImages = async () => {
      const imagePromises = artworks.map((artwork) => {
        return new Promise<void>((resolve) => {
          artworkService.getPublicArtworkBlob(artwork.artwork_id, 'image')
            .then(() => resolve())
            .catch(() => resolve());
        });
      });

      await Promise.all(imagePromises);
      setIsLoading(false);
    };

    preloadImages();
  }, [artworks]);

  const goToPrevious = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!artworks || artworks.length <= 1) return;
    setCurrentIndex((prev) => (prev === 0 ? artworks.length - 1 : prev - 1));
  }, [artworks]);

  const goToNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!artworks || artworks.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % artworks.length);
  }, [artworks]);

  const goToIndex = useCallback((index: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex(index);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key >= '1' && e.key <= '9') {
        const index = parseInt(e.key) - 1;
        if (index < artworks.length) {
          goToIndex(index);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToPrevious, goToNext, goToIndex, artworks]);

  if (!artworks || artworks.length === 0) {
    return (
      <HeroContainer className={className}>
        <LoadingPlaceholder>
          <LoadingIcon
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <PaletteMark />
          </LoadingIcon>
          <LoadingText>Loading featured artworks...</LoadingText>
        </LoadingPlaceholder>
      </HeroContainer>
    );
  }

  const currentArtwork = artworks[currentIndex];

  return (
    <HeroContainer 
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="region"
      aria-label="Featured artwork slideshow"
    >
      {/* Artwork Images */}
      <AnimatePresence mode="wait">
        {!isLoading && (
          <ArtworkImage
            key={`${currentArtwork.artwork_id}-${currentIndex}`}
            artworkId={currentArtwork.artwork_id}
            variant="image"
            updatedAt={currentArtwork.updated_at}
            access="public"
            lazy={false}
            alt={`Artwork by ${currentArtwork.student_name || 'Unknown Student'}`}
          />
        )}
      </AnimatePresence>

      {/* Loading State */}
      {isLoading && (
        <LoadingPlaceholder>
          <LoadingIcon
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <PaletteMark />
          </LoadingIcon>
          <LoadingText>Loading featured artworks...</LoadingText>
        </LoadingPlaceholder>
      )}

      {/* Content Overlay */}
      {!isLoading && (
        <ContentOverlay>
          <ExhibitionBadge
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Featured Exhibition
          </ExhibitionBadge>
          
          <ExhibitionTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            {exhibitionTitle}
          </ExhibitionTitle>
          
          <StudentName
            key={currentArtwork.student_name} // Re-animate on artwork change
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            Artwork by {currentArtwork.student_name || 'Unknown Student'}
          </StudentName>
        </ContentOverlay>
      )}

      {/* Navigation Arrows - Desktop Only */}
      {!isLoading && artworks.length > 1 && (
        <NavigationArrows>
          <ArrowButton
            onClick={goToPrevious}
            aria-label="Previous artwork"
          >
            ‹
          </ArrowButton>
          <ArrowButton
            onClick={goToNext}
            aria-label="Next artwork"
          >
            ›
          </ArrowButton>
        </NavigationArrows>
      )}

      {/* Navigation Dots */}
      {!isLoading && artworks.length > 1 && (
        <NavigationDots role="tablist" aria-label="Artwork navigation">
          {artworks.map((_, index) => (
            <NavigationDot
              key={index}
              $isActive={index === currentIndex}
              onClick={(e) => goToIndex(index, e)}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Go to artwork ${index + 1}`}
            />
          ))}
        </NavigationDots>
      )}
    </HeroContainer>
  );
}
